/**
 * The Legal holds tab's types and rules (owner decision 2026-10-16, migration 370).
 * Pure: no imports, so the render tests and root jest can load it without the
 * admin panel's auth provider.
 */

/** One legal hold in the list. No member data: that is read one hold at a time, audited. */
export interface LegalHold {
  photo_id: string;
  user_id: string;
  decided_at: string;
  decided_by: string | null;
  reasons: string[];
  /** False for a hold set before 2026-10-16: no member data was kept. */
  has_subject: boolean;
  captured_at: string | null;
  report_id: string | null;
  reported_at: string | null;
  delete_after: string | null;
}

export interface LegalHoldList {
  data: LegalHold[];
}

/** The member data kept for the CyberTipline report (POST …/subject, audited). */
export interface LegalHoldSubject {
  photo_id: string;
  user_id: string;
  email: string | null;
  phone_e164: string | null;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  birthday: string | null;
  city: string | null;
  /** The country on the member's profile. */
  country: string | null;
  /**
   * The network region of the member's last request: 'US', 'EEA' or null. It is a
   * jurisdiction bloc from the WAF headers (api-waf.ts inserts only these two values), not a
   * country. Show it with networkRegionText().
   */
  last_request_country: string | null;
  captured_at: string;
  report_id: string | null;
  reported_at: string | null;
  delete_after: string | null;
}

/** A sentence for every code the legal-hold routes send. */
export const LEGAL_HOLD_ERROR_TEXT: Record<string, string> = {
  reason_required: 'Say why you need to see the member data (at least 5 characters).',
  legal_hold_subject_not_found: 'No member data was kept for this legal hold.',
  report_id_invalid: 'Enter the CyberTipline report id: letters, digits or "-", at most 64.',
  report_already_recorded: 'A different report id is already recorded for this legal hold.',
};

/** The shape the server and migration 370 accept. */
const REPORT_ID_SHAPE = /^[A-Za-z0-9-]{1,64}$/;

/** Null when the report id is acceptable; else the sentence to show. */
export function reportIdProblem(raw: string): string | null {
  return REPORT_ID_SHAPE.test(raw.trim()) ? null : LEGAL_HOLD_ERROR_TEXT.report_id_invalid;
}

const NETWORK_REGION_TEXT: Record<string, string> = {
  US: 'United States (US)',
  EEA: 'European Economic Area (EEA)',
};

/** The network region in words. Null: the WAF detected neither region. Any other value as is. */
export function networkRegionText(code: string | null): string {
  if (!code) return 'Not detected (only US and EEA are detected)';
  return NETWORK_REGION_TEXT[code] ?? code;
}

export type ReportTone = 'done' | 'todo' | 'overdue' | 'none';

const day = (iso: string) => iso.slice(0, 10);
const DAY_MS = 24 * 60 * 60 * 1000;

/** What the operator reads about the report: done, act today, overdue, or no data kept. */
export function reportState(hold: LegalHold, now: Date): { tone: ReportTone; text: string } {
  if (!hold.has_subject) {
    return { tone: 'none', text: 'No member data was kept for this hold (it was set before 2026-10-16). Use the account id to find the member.' };
  }
  if (hold.report_id && hold.reported_at && hold.delete_after) {
    return {
      tone: 'done',
      text: `Reported to the NCMEC CyberTipline: report ${hold.report_id} on ${day(hold.reported_at)}. The member data is deleted on ${day(hold.delete_after)}.`,
    };
  }
  const since = new Date(hold.captured_at ?? hold.decided_at).getTime();
  const days = Math.floor((now.getTime() - since) / DAY_MS);
  if (days >= 2) {
    return { tone: 'overdue', text: `Not reported for ${days} days. The daily check sends an alert until you record the report id.` };
  }
  return { tone: 'todo', text: 'Not reported yet. Report it to the NCMEC CyberTipline today, then record the report id here.' };
}
